//
//  helpPages.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module catalogues every routed page for help coverage.
//

/** Audit author recorded on help seeded as a draft; see the Seed Help Topics migrations. */
export const helpSeedAuthor = "help-seed";

/** The shared shell a page is built from, which decides the default tour stops it offers. */
export type HelpPageKind = "collection" | "form" | "lookup" | "custom";

/**
 * Describes one routed page for help coverage.
 */
export type HelpPage = {
    /** Page name shown in the coverage tracker and used as the seeded help title. */
    title: string;
    /** Tracker grouping, following the site's navigation areas. */
    area: string;
    kind: HelpPageKind;
    /**
     * The route whose help and tour this page shares. Set when several routes render the same page
     * in the same mode: version entry, or the same page in another context. Create pages never
     * share with edit pages.
     */
    helpRouteKey?: string;
    /**
     * The ID-parameter route this create page is reached through, for pages that create a record
     * when the ID is `create` rather than through a route of their own.
     */
    createOf?: string;
    /** The page is transient or explanatory, so it has no help topic. */
    helpNotNeeded?: boolean;
};

/**
 * Every page route the router declares, keyed by route pattern in declaration order.
 *
 * Maintenance: a behaviour test compares these keys with the router (plus the create keys reached
 * through an ID parameter), so a new or renamed route fails validation until it is catalogued here. Give a new page its own entry, or point it at an
 * existing page with helpRouteKey when it renders the same page.
 */
const helpPages: Readonly<Record<string, HelpPage>> = {
    "/dashboard": { title: "Dashboard", area: "Dashboard", kind: "custom" },
    "/error": { title: "Error", area: "Other", kind: "custom", helpNotNeeded: true },
    "/error/:errorCode": { title: "Error", area: "Other", kind: "custom", helpNotNeeded: true },
    "/emails/:emailLogGuid": {
        title: "Email Log Preview",
        area: "Other",
        kind: "custom",
        helpNotNeeded: true
    },
    "/help": { title: "Help", area: "Other", kind: "custom", helpNotNeeded: true },
    "/trials": { title: "Trials", area: "Trials", kind: "collection" },
    "/trials/create": { title: "Add Trial", area: "Trials", kind: "form" },
    "/trials/:trialId/details": { title: "Trial Details", area: "Trials", kind: "form" },
    "/trials/:trialId/milestones": { title: "Trial Milestones", area: "Trials", kind: "form" },
    "/trials/:trialId/configuration": {
        title: "Trial Configuration",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/qa-program-checklist": {
        title: "QA Program Checklist Configuration",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/capabilities": {
        title: "Trial Capabilities",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/emails": {
        title: "Trial Emails",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/emails/preferences": {
        title: "Trial Personnel Email Preferences",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/tags": {
        title: "Trial Institution Tags",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/participant-tags": {
        title: "Trial Participant Tags",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/api": {
        title: "Trial API",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/configuration/institution-item-templates": {
        title: "Institution Item Templates",
        area: "Trial Configuration",
        kind: "collection"
    },
    "/trials/:trialId/configuration/institution-item-templates/import": {
        title: "Import Institution Item Templates",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/institution-item-templates/:trialInstitutionItemTemplateId": {
        title: "Institution Item Template",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/configuration/institution-item-templates/create": {
        title: "Add Institution Item Template",
        area: "Trial Configuration",
        kind: "form",
        createOf:
            "/trials/:trialId/configuration/institution-item-templates/:trialInstitutionItemTemplateId"
    },
    "/trials/:trialId/configuration/group-item-templates": {
        title: "Trial Group Item Templates",
        area: "Trial Configuration",
        kind: "collection"
    },
    "/trials/:trialId/configuration/group-item-templates/import": {
        title: "Import Trial Group Item Templates",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/group-item-templates/create": {
        title: "Add Trial Group Item Template",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/configuration/group-item-templates/:trialGroupItemTemplateId": {
        title: "Trial Group Item Template",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/configuration/participant-item-templates": {
        title: "Participant Item Templates",
        area: "Trial Configuration",
        kind: "collection"
    },
    "/trials/:trialId/configuration/participant-item-templates/create": {
        title: "Add Participant Item Template",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/configuration/participant-item-templates/import": {
        title: "Import Participant Item Templates",
        area: "Trial Configuration",
        kind: "custom"
    },
    "/trials/:trialId/configuration/participant-item-templates/:patientItemTemplateId": {
        title: "Participant Item Template",
        area: "Trial Configuration",
        kind: "form"
    },
    "/trials/:trialId/trackers": { title: "QA Tracker", area: "Trial QA", kind: "collection" },
    "/trials/:trialId/trackers/:trialTrackerId": {
        title: "Trial Tracker",
        area: "Trial QA",
        kind: "form"
    },
    "/trials/:trialId/trackers/create": {
        title: "Add Trial Tracker",
        area: "Trial QA",
        kind: "form",
        createOf: "/trials/:trialId/trackers/:trialTrackerId"
    },
    "/trials/:trialId/qa-program-checklist": {
        title: "QA Program Checklist",
        area: "Trial QA",
        kind: "collection"
    },
    "/trials/:trialId/notes": { title: "Trial Notes", area: "Trials", kind: "collection" },
    "/trials/:trialId/notes/:noteId": { title: "Trial Note", area: "Trials", kind: "form" },
    "/trials/:trialId/notes/create": {
        title: "Add Trial Note",
        area: "Trials",
        kind: "form",
        createOf: "/trials/:trialId/notes/:noteId"
    },
    "/trials/:trialId/participants": {
        title: "Trial Participants",
        area: "Trial Participants",
        kind: "collection"
    },
    "/trials/:trialId/participants/create": {
        title: "Add Trial Participant",
        area: "Trial Participants",
        kind: "form"
    },
    "/trials/:trialId/participants/:patientId/details": {
        title: "Trial Participant",
        area: "Trial Participants",
        kind: "form"
    },
    "/trials/:trialId/participants/:patientId/administrationcasereport": {
        title: "Participant Administration Case Report",
        area: "Trial Participants",
        kind: "collection"
    },
    "/trials/:trialId/participants/:patientId/emails": {
        title: "Participant Emails",
        area: "Trial Participants",
        kind: "custom"
    },
    "/trials/:trialId/participants/:patientId/items": {
        title: "Participant Items",
        area: "Trial Participants",
        kind: "collection"
    },
    "/trials/:trialId/participants/:patientId/items/create": {
        title: "Add Participant Item",
        area: "Trial Participants",
        kind: "form"
    },
    "/trials/:trialId/participants/:patientId/items/:patientItemId": {
        title: "Participant Item",
        area: "Trial Participants",
        kind: "form"
    },
    "/trials/:trialId/participants/:patientId/items/:patientItemId/entry": {
        title: "Participant Item Entry",
        area: "Trial Participants",
        kind: "custom"
    },
    "/trials/:trialId/participants/:patientId/items/:patientItemId/versions/:patientItemVersionId/entry":
        {
            title: "Participant Item Entry",
            area: "Trial Participants",
            kind: "custom",
            helpRouteKey: "/trials/:trialId/participants/:patientId/items/:patientItemId/entry"
        },
    "/trials/:trialId/personnel": { title: "Trial Personnel", area: "Trials", kind: "collection" },
    "/trials/:trialId/personnel/create": {
        title: "Add Trial Personnel",
        area: "Trials",
        kind: "custom"
    },
    "/trials/:trialId/personnel/:personnelRoleId": {
        title: "Trial Personnel Role",
        area: "Trials",
        kind: "custom"
    },
    "/trials/:trialId/groups": { title: "Trial Groups", area: "Trials", kind: "collection" },
    "/trials/:trialId/groups/:trialGroupId/details": {
        title: "Trial Group",
        area: "Trials",
        kind: "form"
    },
    "/trials/:trialId/groups/create/details": {
        title: "Add Trial Group",
        area: "Trials",
        kind: "form",
        createOf: "/trials/:trialId/groups/:trialGroupId/details"
    },
    "/trials/:trialId/groups/:trialGroupId/items": {
        title: "Trial Group Items",
        area: "Trials",
        kind: "collection"
    },
    "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/details": {
        title: "Trial Group Item",
        area: "Trials",
        kind: "form"
    },
    "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/entry": {
        title: "Trial Group Item Data Entry",
        area: "Trials",
        kind: "custom"
    },
    "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/versions/:trialGroupItemVersionId/entry":
        {
            title: "Trial Group Item Data Entry",
            area: "Trials",
            kind: "custom",
            helpRouteKey: "/trials/:trialId/groups/:trialGroupId/items/:trialGroupItemId/entry"
        },
    "/trials/:trialId/treatments": {
        title: "Trial Treatments",
        area: "Trials",
        kind: "collection"
    },
    "/trials/:trialId/treatments/:trialTreatmentId": {
        title: "Trial Treatment",
        area: "Trials",
        kind: "form"
    },
    "/trials/:trialId/treatments/create": {
        title: "Add Trial Treatment",
        area: "Trials",
        kind: "form",
        createOf: "/trials/:trialId/treatments/:trialTreatmentId"
    },
    "/trials/:trialId/forms": { title: "Trial Forms", area: "Trial QA", kind: "collection" },
    "/trials/:trialId/forms/:trialFormId": { title: "Trial Form", area: "Trial QA", kind: "form" },
    "/trials/:trialId/forms/create": {
        title: "Add Trial Form",
        area: "Trial QA",
        kind: "form",
        createOf: "/trials/:trialId/forms/:trialFormId"
    },
    "/trials/:trialId/forms/:trialFormId/report": {
        title: "QA Case Report",
        area: "Trial QA",
        kind: "form"
    },
    "/trials/:trialId/forms/:trialFormId/report/edit": {
        title: "Edit QA Case Report",
        area: "Trial QA",
        kind: "form"
    },
    "/trials/:trialId/forms/:trialFormId/administrationcasereport": {
        title: "Administration Case Report",
        area: "Trial QA",
        kind: "collection"
    },
    "/trials/:trialId/forms/:trialFormId/emails": {
        title: "Trial Form Emails",
        area: "Trial QA",
        kind: "custom"
    },
    "/trials/:trialId/forms/:trialFormId/entry": {
        title: "Trial Form Data Entry",
        area: "Trial QA",
        kind: "custom"
    },
    "/trials/:trialId/forms/:trialFormId/versions/:trialFormVersionId/entry": {
        title: "Trial Form Data Entry",
        area: "Trial QA",
        kind: "custom",
        helpRouteKey: "/trials/:trialId/forms/:trialFormId/entry"
    },
    "/trials/:trialId/items": { title: "Items", area: "Trial Items", kind: "custom" },
    "/trials/:trialId/items/all-items": {
        title: "All Items",
        area: "Trial Items",
        kind: "collection"
    },
    "/trials/:trialId/items/trial-items": {
        title: "Trial Item List",
        area: "Trial Items",
        kind: "collection"
    },
    "/trials/:trialId/items/trial-items/create": {
        title: "Add Trial Item",
        area: "Trial Items",
        kind: "form"
    },
    "/trials/:trialId/items/trial-items/import": {
        title: "Import Trial Items",
        area: "Trial Items",
        kind: "custom"
    },
    "/trials/:trialId/items/trial-items/:trialItemId": {
        title: "Trial Item",
        area: "Trial Items",
        kind: "form"
    },
    "/trials/:trialId/items/trial-items/:trialItemId/entry": {
        title: "Trial Item Entry",
        area: "Trial Items",
        kind: "custom"
    },
    "/trials/:trialId/items/trial-items/:trialItemId/versions/:trialItemVersionId/entry": {
        title: "Trial Item Entry",
        area: "Trial Items",
        kind: "custom",
        helpRouteKey: "/trials/:trialId/items/trial-items/:trialItemId/entry"
    },
    "/trials/:trialId/items/institution-items": {
        title: "Institution Items",
        area: "Trial Items",
        kind: "collection"
    },
    "/trials/:trialId/items/institution-items/update": {
        title: "Bulk Update Institution Items",
        area: "Trial Items",
        kind: "custom"
    },
    "/trials/:trialId/items/group-items": {
        title: "Group Items",
        area: "Trial Items",
        kind: "collection"
    },
    "/trials/:trialId/items/group-items/update": {
        title: "Bulk Update Group Items",
        area: "Trial Items",
        kind: "custom"
    },
    "/trials/:trialId/items/participant-items": {
        title: "Participant Items",
        area: "Trial Items",
        kind: "collection",
        helpRouteKey: "/trials/:trialId/participants/:patientId/items"
    },
    "/trials/:trialId/items/participant-items/update": {
        title: "Bulk Update Participant Items",
        area: "Trial Items",
        kind: "custom"
    },
    "/trials/:trialId/institutions": {
        title: "Trial Institutions",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/create": {
        title: "Add Trial Institution",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/details": {
        title: "Trial Institution",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites": {
        title: "Supporting Sites",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites/create": {
        title: "Add Supporting Site",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites/:supportingSiteId/details":
        { title: "Supporting Site", area: "Trial Institutions", kind: "form" },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites/:supportingSiteId/personnel":
        { title: "Supporting Site Personnel", area: "Trial Institutions", kind: "collection" },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites/:supportingSiteId/personnel/create":
        { title: "Add Supporting Site Personnel", area: "Trial Institutions", kind: "custom" },
    "/trials/:trialId/institutions/:trialInstitutionId/supporting-sites/:supportingSiteId/personnel/:personnelRoleId":
        { title: "Supporting Site Personnel Role", area: "Trial Institutions", kind: "custom" },
    "/trials/:trialId/institutions/:trialInstitutionId/capabilities": {
        title: "Capabilities",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/emails": {
        title: "Trial Institution Emails",
        area: "Trial Institutions",
        kind: "custom"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants": {
        title: "Trial Institution Participants",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/create": {
        title: "Add Participant",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/details": {
        title: "Participant",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/administrationcasereport":
        {
            title: "Participant Administration Case Report",
            area: "Trial Institutions",
            kind: "collection",
            helpRouteKey: "/trials/:trialId/participants/:patientId/administrationcasereport"
        },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/emails": {
        title: "Participant Emails",
        area: "Trial Institutions",
        kind: "custom",
        helpRouteKey: "/trials/:trialId/participants/:patientId/emails"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/items": {
        title: "Participant Items",
        area: "Trial Institutions",
        kind: "collection",
        helpRouteKey: "/trials/:trialId/participants/:patientId/items"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/items/create": {
        title: "Add Participant Item",
        area: "Trial Institutions",
        kind: "form",
        helpRouteKey: "/trials/:trialId/participants/:patientId/items/create"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/items/:patientItemId":
        {
            title: "Participant Item",
            area: "Trial Institutions",
            kind: "form",
            helpRouteKey: "/trials/:trialId/participants/:patientId/items/:patientItemId"
        },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/items/:patientItemId/entry":
        {
            title: "Participant Item Entry",
            area: "Trial Institutions",
            kind: "custom",
            helpRouteKey: "/trials/:trialId/participants/:patientId/items/:patientItemId/entry"
        },
    "/trials/:trialId/institutions/:trialInstitutionId/participants/:patientId/items/:patientItemId/versions/:patientItemVersionId/entry":
        {
            title: "Participant Item Entry",
            area: "Trial Institutions",
            kind: "custom",
            helpRouteKey: "/trials/:trialId/participants/:patientId/items/:patientItemId/entry"
        },
    "/trials/:trialId/institutions/:trialInstitutionId/items": {
        title: "Trial Institution Items",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/items/:trialInstitutionItemId": {
        title: "Trial Institution Item",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/items/create": {
        title: "Add Trial Institution Item",
        area: "Trial Institutions",
        kind: "form",
        createOf: "/trials/:trialId/institutions/:trialInstitutionId/items/:trialInstitutionItemId"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/items/:trialInstitutionItemId/entry": {
        title: "Trial Institution Item Entry",
        area: "Trial Institutions",
        kind: "custom"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/items/:trialInstitutionItemId/versions/:trialInstitutionItemVersionId/entry":
        {
            title: "Trial Institution Item Entry",
            area: "Trial Institutions",
            kind: "custom",
            helpRouteKey:
                "/trials/:trialId/institutions/:trialInstitutionId/items/:trialInstitutionItemId/entry"
        },
    "/trials/:trialId/institutions/:trialInstitutionId/personnel": {
        title: "Trial Institution Personnel",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/personnel/create": {
        title: "Add Trial Institution Personnel",
        area: "Trial Institutions",
        kind: "custom"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/personnel/:personnelRoleId": {
        title: "Trial Institution Personnel Role",
        area: "Trial Institutions",
        kind: "custom"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/notes": {
        title: "Trial Institution Notes",
        area: "Trial Institutions",
        kind: "collection"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/notes/:noteId": {
        title: "Trial Institution Note",
        area: "Trial Institutions",
        kind: "form"
    },
    "/trials/:trialId/institutions/:trialInstitutionId/notes/create": {
        title: "Add Trial Institution Note",
        area: "Trial Institutions",
        kind: "form",
        createOf: "/trials/:trialId/institutions/:trialInstitutionId/notes/:noteId"
    },
    "/institutions": { title: "Institutions", area: "Institutions", kind: "collection" },
    "/institutions/create": { title: "Add Institution", area: "Institutions", kind: "form" },
    "/institutions/:institutionId/details": {
        title: "Institution Details",
        area: "Institutions",
        kind: "form"
    },
    "/institutions/:institutionId/forms": {
        title: "Institution Forms",
        area: "Institutions",
        kind: "collection"
    },
    "/institutions/:institutionId/forms/:institutionFormId": {
        title: "Institution Form",
        area: "Institutions",
        kind: "form"
    },
    "/institutions/:institutionId/forms/create": {
        title: "Add Institution Form",
        area: "Institutions",
        kind: "form",
        createOf: "/institutions/:institutionId/forms/:institutionFormId"
    },
    "/institutions/:institutionId/forms/:institutionFormId/entry": {
        title: "Institution Form Entry",
        area: "Institutions",
        kind: "custom"
    },
    "/institutions/:institutionId/forms/:institutionFormId/versions/:institutionFormVersionId/entry":
        {
            title: "Institution Form Entry",
            area: "Institutions",
            kind: "custom",
            helpRouteKey: "/institutions/:institutionId/forms/:institutionFormId/entry"
        },
    "/institutions/:institutionId/emails": {
        title: "Institution Emails",
        area: "Institutions",
        kind: "custom"
    },
    "/institutions/:institutionId/notes": {
        title: "Institution Notes",
        area: "Institutions",
        kind: "collection"
    },
    "/institutions/:institutionId/notes/:noteId": {
        title: "Institution Note",
        area: "Institutions",
        kind: "form"
    },
    "/institutions/:institutionId/notes/create": {
        title: "Add Institution Note",
        area: "Institutions",
        kind: "form",
        createOf: "/institutions/:institutionId/notes/:noteId"
    },
    "/institutions/:institutionId/personnel": {
        title: "Institution Personnel",
        area: "Institutions",
        kind: "collection"
    },
    "/institutions/:institutionId/personnel/create": {
        title: "Add Institution Personnel",
        area: "Institutions",
        kind: "custom"
    },
    "/institutions/:institutionId/personnel/:personnelRoleId": {
        title: "Institution Personnel Role",
        area: "Institutions",
        kind: "custom"
    },
    "/institutions/:institutionId/departments": {
        title: "Institution Departments",
        area: "Institutions",
        kind: "collection"
    },
    "/institutions/:institutionId/departments/create": {
        title: "Add Institution Department",
        area: "Institutions",
        kind: "form"
    },
    "/institutions/:institutionId/departments/:departmentId/details": {
        title: "Institution Department",
        area: "Institutions",
        kind: "form"
    },
    "/institutions/:institutionId/departments/:departmentId/personnel": {
        title: "Department Personnel",
        area: "Institutions",
        kind: "collection"
    },
    "/institutions/:institutionId/departments/:departmentId/personnel/create": {
        title: "Add Department Personnel",
        area: "Institutions",
        kind: "custom"
    },
    "/institutions/:institutionId/departments/:departmentId/personnel/:personnelRoleId": {
        title: "Department Personnel Role",
        area: "Institutions",
        kind: "custom"
    },
    "/groups": { title: "Groups", area: "Groups", kind: "collection" },
    "/groups/:groupId/details": { title: "Group Details", area: "Groups", kind: "form" },
    "/groups/create/details": {
        title: "Add Group",
        area: "Groups",
        kind: "form",
        createOf: "/groups/:groupId/details"
    },
    "/groups/:groupId/notes": { title: "Group Notes", area: "Groups", kind: "collection" },
    "/groups/:groupId/notes/:noteId": { title: "Group Note", area: "Groups", kind: "form" },
    "/groups/:groupId/notes/create": {
        title: "Add Group Note",
        area: "Groups",
        kind: "form",
        createOf: "/groups/:groupId/notes/:noteId"
    },
    "/groups/:groupId/personnel": { title: "Group Personnel", area: "Groups", kind: "collection" },
    "/groups/:groupId/personnel/create": {
        title: "Add Group Personnel",
        area: "Groups",
        kind: "form"
    },
    "/groups/:groupId/personnel/:personnelRoleId": {
        title: "Group Personnel Role",
        area: "Groups",
        kind: "form"
    },
    "/groups/:groupId/trials": { title: "Group Trials", area: "Groups", kind: "collection" },
    "/groups/:groupId/configuration": {
        title: "Group Configuration",
        area: "Groups",
        kind: "custom"
    },
    "/groups/:groupId/configuration/items": {
        title: "Trial Group Item Templates",
        area: "Groups",
        kind: "collection",
        helpRouteKey: "/trials/:trialId/configuration/group-item-templates"
    },
    "/groups/:groupId/configuration/items/import": {
        title: "Import Trial Group Item Templates",
        area: "Groups",
        kind: "custom",
        helpRouteKey: "/trials/:trialId/configuration/group-item-templates/import"
    },
    "/groups/:groupId/configuration/items/create": {
        title: "Add Trial Group Item Template",
        area: "Groups",
        kind: "form",
        helpRouteKey: "/trials/:trialId/configuration/group-item-templates/create"
    },
    "/groups/:groupId/configuration/items/:trialGroupItemTemplateId": {
        title: "Trial Group Item Template",
        area: "Groups",
        kind: "form",
        helpRouteKey:
            "/trials/:trialId/configuration/group-item-templates/:trialGroupItemTemplateId"
    },
    "/personnel": { title: "Personnel", area: "Personnel", kind: "collection" },
    "/personnel/byroles": { title: "Personnel By Roles", area: "Personnel", kind: "collection" },
    "/personnel/create": { title: "Add Personnel", area: "Personnel", kind: "form" },
    "/personnel/:personnelId/details": {
        title: "Personnel Details",
        area: "Personnel",
        kind: "form"
    },
    "/personnel/:personnelId/notes": {
        title: "Personnel Notes",
        area: "Personnel",
        kind: "collection"
    },
    "/personnel/:personnelId/notes/:noteId": {
        title: "Personnel Note",
        area: "Personnel",
        kind: "form"
    },
    "/personnel/:personnelId/notes/create": {
        title: "Add Personnel Note",
        area: "Personnel",
        kind: "form",
        createOf: "/personnel/:personnelId/notes/:noteId"
    },
    "/personnel/:personnelId/roles": {
        title: "Personnel Roles",
        area: "Personnel",
        kind: "collection"
    },
    "/personnel/:personnelId/roles/:personnelRoleId": {
        title: "Personnel Role",
        area: "Personnel",
        kind: "form"
    },
    "/personnel/:personnelId/roles/create": {
        title: "Add Personnel Role",
        area: "Personnel",
        kind: "form",
        createOf: "/personnel/:personnelId/roles/:personnelRoleId"
    },
    "/personnel/:personnelId/activedirectory": {
        title: "Login & Security",
        area: "Personnel",
        kind: "custom"
    },
    "/personnel/:personnelId/openclinica": {
        title: "Personnel OC Roles",
        area: "Personnel",
        kind: "collection"
    },
    "/personnel/:personnelId/permissions": {
        title: "Personnel Permissions",
        area: "Personnel",
        kind: "collection"
    },
    "/personnel/:personnelId/files": { title: "Personnel Files", area: "Personnel", kind: "form" },
    "/personnel/:personnelId/hubspot": {
        title: "Personnel HubSpot",
        area: "Personnel",
        kind: "custom"
    },
    "/personnel/:personnelId/emails": {
        title: "Personnel Email Preferences",
        area: "Personnel",
        kind: "custom"
    },
    "/settings": { title: "Settings", area: "Settings", kind: "custom" },
    "/settings/general": {
        title: "Settings",
        area: "Settings: General",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/general/countries": {
        title: "Countries",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/countries/create": {
        title: "Add Country",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/countries/:countryId": {
        title: "Country",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/states": { title: "States", area: "Settings: General", kind: "lookup" },
    "/settings/general/states/:stateId": {
        title: "State",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/states/create": {
        title: "Add State",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/states/:stateId"
    },
    "/settings/general/itemcomponenttypes": {
        title: "Item Component Types",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/itemcomponenttypes/:itemComponentTypeId": {
        title: "Item Component Type",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/itemcomponenttypes/create": {
        title: "Add Item Component Type",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/itemcomponenttypes/:itemComponentTypeId"
    },
    "/settings/general/notetypes": {
        title: "Note Types",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/notetypes/:noteTypeId": {
        title: "Note Type",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/notetypes/create": {
        title: "Add Note Type",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/notetypes/:noteTypeId"
    },
    "/settings/general/genders": { title: "Genders", area: "Settings: General", kind: "lookup" },
    "/settings/general/genders/:genderId": {
        title: "Gender",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/genders/create": {
        title: "Add Gender",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/genders/:genderId"
    },
    "/settings/general/interests": {
        title: "Interests",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/interests/:interestId": {
        title: "Interest",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/interests/create": {
        title: "Add Interest",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/interests/:interestId"
    },
    "/settings/general/membershiptypes": {
        title: "Membership Types",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/membershiptypes/:membershipTypeId": {
        title: "Membership Type",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/membershiptypes/create": {
        title: "Add Membership Type",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/membershiptypes/:membershipTypeId"
    },
    "/settings/general/professionaldisciplines": {
        title: "Professional Disciplines",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/professionaldisciplines/:professionalDisciplineId": {
        title: "Professional Discipline",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/professionaldisciplines/create": {
        title: "Add Professional Discipline",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/professionaldisciplines/:professionalDisciplineId"
    },
    "/settings/general/salutations": {
        title: "Salutations",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/salutations/:salutationId": {
        title: "Salutation",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/salutations/create": {
        title: "Add Salutation",
        area: "Settings: General",
        kind: "lookup",
        createOf: "/settings/general/salutations/:salutationId"
    },
    "/settings/general/roles": { title: "Roles", area: "Settings: General", kind: "lookup" },
    "/settings/general/roles/create": {
        title: "Add Role",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/roles/:roleId/details": {
        title: "Role",
        area: "Settings: General",
        kind: "lookup"
    },
    "/settings/general/roles/:roleId/permissions": {
        title: "Role Permissions",
        area: "Settings: General",
        kind: "collection"
    },
    "/settings/general/roles/:roleId/permissions/create": {
        title: "Add Role Permissions",
        area: "Settings: General",
        kind: "form"
    },
    "/settings/general/roles/:roleId/permissions/:permissionRequirementRoleId": {
        title: "Role Permission",
        area: "Settings: General",
        kind: "form"
    },
    "/settings/general/notices": {
        title: "Notices",
        area: "Settings: General",
        kind: "collection"
    },
    "/settings/general/notices/create": {
        title: "Add Notice",
        area: "Settings: General",
        kind: "form"
    },
    "/settings/general/notices/:noticeId": {
        title: "Notice",
        area: "Settings: General",
        kind: "form"
    },
    "/settings/general/emails": {
        title: "Email Defaults",
        area: "Settings: General",
        kind: "custom"
    },
    "/settings/general/help": {
        title: "Help Coverage",
        area: "Settings: General",
        kind: "collection"
    },
    "/settings/hubspot": {
        title: "Settings",
        area: "Settings: HubSpot",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/hubspot/activemembers": {
        title: "HubSpot Active Members",
        area: "Settings: HubSpot",
        kind: "custom"
    },
    "/settings/hubspot/import": {
        title: "Process New HubSpot Members",
        area: "Settings: HubSpot",
        kind: "custom"
    },
    "/settings/hubspot/emailupdate": {
        title: "HubSpot Email Maintenance",
        area: "Settings: HubSpot",
        kind: "custom"
    },
    "/settings/openclinicauserroles": {
        title: "OpenClinica User Roles",
        area: "Settings: General",
        kind: "collection"
    },
    "/settings/institution": {
        title: "Settings",
        area: "Settings: Institutions",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/institution/sitelocations": {
        title: "Site Locations",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/sitelocations/:siteLocationId": {
        title: "Site Location",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/sitelocations/create": {
        title: "Add Site Location",
        area: "Settings: Institutions",
        kind: "lookup",
        createOf: "/settings/institution/sitelocations/:siteLocationId"
    },
    "/settings/institution/facilityalliancemembers": {
        title: "Facility Alliance Members",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/facilityalliancemembers/:facilityAllianceMemberId": {
        title: "Facility Alliance Member",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/facilityalliancemembers/create": {
        title: "Add Facility Alliance Member",
        area: "Settings: Institutions",
        kind: "lookup",
        createOf: "/settings/institution/facilityalliancemembers/:facilityAllianceMemberId"
    },
    "/settings/institution/institutiontypes": {
        title: "Institution Types",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/institutiontypes/:institutionTypeId": {
        title: "Institution Type",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/institutiontypes/create": {
        title: "Add Institution Type",
        area: "Settings: Institutions",
        kind: "lookup",
        createOf: "/settings/institution/institutiontypes/:institutionTypeId"
    },
    "/settings/institution/capability-categories": {
        title: "Institution Capability Categories",
        area: "Settings: Institutions",
        kind: "collection"
    },
    "/settings/institution/capability-categories/create": {
        title: "Add Institution Capability Category",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/capability-categories/:institutionCapabilityCategoryId": {
        title: "Institution Capability Category",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/capabilities": {
        title: "Institution Capabilities",
        area: "Settings: Institutions",
        kind: "collection"
    },
    "/settings/institution/capabilities/create": {
        title: "Add Institution Capability",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/capabilities/:institutionCapabilityId": {
        title: "Institution Capability",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/tag-categories": {
        title: "Institution Tag Categories",
        area: "Settings: Institutions",
        kind: "collection"
    },
    "/settings/institution/tag-categories/create": {
        title: "Add Institution Tag Category",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/tag-categories/:institutionTagCategoryId": {
        title: "Institution Tag Category",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/tags": {
        title: "Institution Tags",
        area: "Settings: Institutions",
        kind: "collection"
    },
    "/settings/institution/tags/create": {
        title: "Add Institution Tag",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/tags/:institutionTagId": {
        title: "Institution Tag",
        area: "Settings: Institutions",
        kind: "form"
    },
    "/settings/institution/institutioncodings": {
        title: "Institution Codings",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/institutioncodings/:institutionCodingId": {
        title: "Institution Coding",
        area: "Settings: Institutions",
        kind: "lookup"
    },
    "/settings/institution/institutioncodings/create": {
        title: "Add Institution Coding",
        area: "Settings: Institutions",
        kind: "lookup",
        createOf: "/settings/institution/institutioncodings/:institutionCodingId"
    },
    "/settings/participant": {
        title: "Settings",
        area: "Settings: Participants",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/participant/tag-categories": {
        title: "Participant Tag Categories",
        area: "Settings: Participants",
        kind: "collection"
    },
    "/settings/participant/tag-categories/create": {
        title: "Add Participant Tag Category",
        area: "Settings: Participants",
        kind: "form"
    },
    "/settings/participant/tag-categories/:patientTagCategoryId": {
        title: "Participant Tag Category",
        area: "Settings: Participants",
        kind: "form"
    },
    "/settings/participant/tags": {
        title: "Participant Tags",
        area: "Settings: Participants",
        kind: "collection"
    },
    "/settings/participant/tags/create": {
        title: "Add Participant Tag",
        area: "Settings: Participants",
        kind: "form"
    },
    "/settings/participant/tags/:patientTagId": {
        title: "Participant Tag",
        area: "Settings: Participants",
        kind: "form"
    },
    "/settings/trial": {
        title: "Settings",
        area: "Settings: Trials",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/trial/trialcategorys": {
        title: "Trial Categories",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialcategorys/:trialCategoryId": {
        title: "Trial Category",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialcategorys/create": {
        title: "Add Trial Category",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialcategorys/:trialCategoryId"
    },
    "/settings/trial/trialtypes": {
        title: "Trial Types",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialtypes/:trialTypeId": {
        title: "Trial Type",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialtypes/create": {
        title: "Add Trial Type",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialtypes/:trialTypeId"
    },
    "/settings/trial/trialphases": {
        title: "Trial Phases",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialphases/:trialPhaseId": {
        title: "Trial Phase",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialphases/create": {
        title: "Add Trial Phase",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialphases/:trialPhaseId"
    },
    "/settings/trial/tumourgroups": {
        title: "Tumour Groups",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/tumourgroups/:tumourGroupId": {
        title: "Tumour Group",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/tumourgroups/create": {
        title: "Add Tumour Group",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/tumourgroups/:tumourGroupId"
    },
    "/settings/trial/trialPresentationTypes": {
        title: "Trial Presentation Types",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialPresentationTypes/:trialPresentationTypeId": {
        title: "Trial Presentation Type",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialPresentationTypes/create": {
        title: "Add Trial Presentation Type",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialPresentationTypes/:trialPresentationTypeId"
    },
    "/settings/trial/trialPublicationTypes": {
        title: "Trial Publication Types",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialPublicationTypes/:trialPublicationTypeId": {
        title: "Trial Publication Type",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialPublicationTypes/create": {
        title: "Add Trial Publication Type",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialPublicationTypes/:trialPublicationTypeId"
    },
    "/settings/trial/trialAnalysisTypes": {
        title: "Trial Analysis Types",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialAnalysisTypes/:trialAnalysisTypeId": {
        title: "Trial Analysis Type",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/trialAnalysisTypes/create": {
        title: "Add Trial Analysis Type",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/trialAnalysisTypes/:trialAnalysisTypeId"
    },
    "/settings/trial/involvements": {
        title: "Involvements",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/involvements/:involvementId": {
        title: "Involvement",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/involvements/create": {
        title: "Add Involvement",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/involvements/:involvementId"
    },
    "/settings/trial/allocatedwps": {
        title: "Allocated WPs",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/allocatedwps/:allocatedWPId": {
        title: "Allocated WP",
        area: "Settings: Trials",
        kind: "lookup"
    },
    "/settings/trial/allocatedwps/create": {
        title: "Add Allocated WP",
        area: "Settings: Trials",
        kind: "lookup",
        createOf: "/settings/trial/allocatedwps/:allocatedWPId"
    },
    "/settings/qa": {
        title: "Settings",
        area: "Settings: QA",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/qa/formtemplates": { title: "Form Templates", area: "Settings: QA", kind: "lookup" },
    "/settings/qa/formtemplates/:formTemplateId": {
        title: "Form Template",
        area: "Settings: QA",
        kind: "form"
    },
    "/settings/qa/formtemplates/create": {
        title: "Add Form Template",
        area: "Settings: QA",
        kind: "form",
        createOf: "/settings/qa/formtemplates/:formTemplateId"
    },
    "/settings/qa/program-checklist": {
        title: "QA Program Checklist Defaults",
        area: "Settings: QA",
        kind: "custom"
    },
    "/settings/qa/qaEmailTemplates": {
        title: "QA Email Templates",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/qaEmailTemplates/:standardEmailTemplateId": {
        title: "QA Email Template",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/qaEmailTemplates/create": {
        title: "Add QA Email Template",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/qaEmailTemplates/:standardEmailTemplateId"
    },
    "/settings/qa/formcomponents": {
        title: "Form Components",
        area: "Settings: QA",
        kind: "collection"
    },
    "/settings/qa/formcomponents/:itemComponentId": {
        title: "Form Component",
        area: "Settings: QA",
        kind: "custom"
    },
    "/settings/qa/formcomponents/create": {
        title: "Add Form Component",
        area: "Settings: QA",
        kind: "custom",
        createOf: "/settings/qa/formcomponents/:itemComponentId"
    },
    "/settings/qa/formcomponents/:itemComponentId/:cloneId": {
        title: "Form Component",
        area: "Settings: QA",
        kind: "custom",
        helpRouteKey: "/settings/qa/formcomponents/:itemComponentId"
    },
    "/settings/qa/reportingTagCategories": {
        title: "Reporting Tag Categories",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/reportingTagCategories/:reportingTagCategoryId": {
        title: "Reporting Tag Category",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/reportingTagCategories/create": {
        title: "Add Reporting Tag Category",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/reportingTagCategories/:reportingTagCategoryId"
    },
    "/settings/qa/reportingTags": { title: "Reporting Tags", area: "Settings: QA", kind: "lookup" },
    "/settings/qa/reportingTags/:reportingTagId": {
        title: "Reporting Tag",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/reportingTags/create": {
        title: "Add Reporting Tag",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/reportingTags/:reportingTagId"
    },
    "/settings/qa/taskTypes": { title: "Task Types", area: "Settings: QA", kind: "lookup" },
    "/settings/qa/taskTypes/:taskTypeId": {
        title: "Task Type",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/taskTypes/create": {
        title: "Add Task Type",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/taskTypes/:taskTypeId"
    },
    "/settings/qa/trackerTypes": { title: "Tracker Types", area: "Settings: QA", kind: "lookup" },
    "/settings/qa/trackerTypes/:trackerTypeId": {
        title: "Tracker Type",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/trackerTypes/create": {
        title: "Add Tracker Type",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/trackerTypes/:trackerTypeId"
    },
    "/settings/qa/trackerMilestones": {
        title: "Tracker Milestones",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/trackerMilestones/:trackerMilestoneId": {
        title: "Tracker Milestone",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/trackerMilestones/create": {
        title: "Add Tracker Milestone",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/trackerMilestones/:trackerMilestoneId"
    },
    "/settings/qa/formTemplateFileTypes": {
        title: "Form Template File Types",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/formTemplateFileTypes/:formTemplateFileTypeId": {
        title: "Form Template File Type",
        area: "Settings: QA",
        kind: "lookup"
    },
    "/settings/qa/formTemplateFileTypes/create": {
        title: "Add Form Template File Type",
        area: "Settings: QA",
        kind: "lookup",
        createOf: "/settings/qa/formTemplateFileTypes/:formTemplateFileTypeId"
    },
    "/settings/item": {
        title: "Settings",
        area: "Settings: Items",
        kind: "custom",
        helpRouteKey: "/settings"
    },
    "/settings/item/itemcomponents": {
        title: "Item Components",
        area: "Settings: Items",
        kind: "collection"
    },
    "/settings/item/itemcomponents/:itemComponentId": {
        title: "Item Component",
        area: "Settings: Items",
        kind: "custom"
    },
    "/settings/item/itemcomponents/create": {
        title: "Add Item Component",
        area: "Settings: Items",
        kind: "custom",
        createOf: "/settings/item/itemcomponents/:itemComponentId"
    },
    "/settings/item/itemcomponents/:itemComponentId/:cloneId": {
        title: "Item Component",
        area: "Settings: Items",
        kind: "custom",
        helpRouteKey: "/settings/item/itemcomponents/:itemComponentId"
    },
    "/settings/item/itemcategories": {
        title: "Categories",
        area: "Settings: Items",
        kind: "lookup"
    },
    "/settings/item/itemcategories/:categoryId": {
        title: "Category",
        area: "Settings: Items",
        kind: "lookup"
    },
    "/settings/item/itemcategories/create": {
        title: "Add Category",
        area: "Settings: Items",
        kind: "lookup",
        createOf: "/settings/item/itemcategories/:categoryId"
    },
    "/settings/item/itemtemplates": {
        title: "Trial Institution Item Templates (Global)",
        area: "Settings: Items",
        kind: "collection"
    },
    "/settings/item/itemtemplates/:trialInstitutionItemTemplateId": {
        title: "Trial Institution Item Template (Global)",
        area: "Settings: Items",
        kind: "form"
    },
    "/settings/item/itemtemplates/create": {
        title: "Add Trial Institution Item Template (Global)",
        area: "Settings: Items",
        kind: "form",
        createOf: "/settings/item/itemtemplates/:trialInstitutionItemTemplateId"
    },
    "/settings/item/participant-item-templates": {
        title: "Participant Item Templates (Global)",
        area: "Settings: Items",
        kind: "collection"
    },
    "/settings/item/participant-item-templates/create": {
        title: "Add Participant Item Template (Global)",
        area: "Settings: Items",
        kind: "form"
    },
    "/settings/item/participant-item-templates/:patientItemTemplateId": {
        title: "Participant Item Template (Global)",
        area: "Settings: Items",
        kind: "form"
    },
    "/settings/item/group-item-templates": {
        title: "Trial Group Item Templates",
        area: "Settings: Items",
        kind: "collection",
        helpRouteKey: "/trials/:trialId/configuration/group-item-templates"
    },
    "/settings/item/group-item-templates/create": {
        title: "Add Trial Group Item Template",
        area: "Settings: Items",
        kind: "form",
        helpRouteKey: "/trials/:trialId/configuration/group-item-templates/create"
    },
    "/settings/item/group-item-templates/:trialGroupItemTemplateId": {
        title: "Trial Group Item Template",
        area: "Settings: Items",
        kind: "form",
        helpRouteKey:
            "/trials/:trialId/configuration/group-item-templates/:trialGroupItemTemplateId"
    },
    "/settings/item/trial-item-templates": {
        title: "Trial Item Templates",
        area: "Settings: Items",
        kind: "collection"
    },
    "/settings/item/trial-item-templates/:trialItemTemplateId": {
        title: "Trial Item Template",
        area: "Settings: Items",
        kind: "form"
    },
    "/settings/item/trial-item-templates/create": {
        title: "Add Trial Item Template",
        area: "Settings: Items",
        kind: "form",
        createOf: "/settings/item/trial-item-templates/:trialItemTemplateId"
    }
};

/**
 * Returns the route that owns the help and tour for a route pattern.
 *
 * Postconditions: Uncatalogued routes own their own help.
 */
export const getHelpRouteKey = (routeKey: string) => helpPages[routeKey]?.helpRouteKey ?? routeKey;

export default helpPages;
